import React from 'react'
import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MetaItem { label: string; value: string }

interface ValueCardProps {
  title: string
  value: string
  subtitle?: string
  icon?: LucideIcon
  color?: 'primary' | 'success' | 'danger' | 'warning' | 'info'
  badge?: string
  metadata?: MetaItem[]
  className?: string
}

const colorConfig: Record<string, { icon: string; badge: string; value: string }> = {
  primary: {
    icon: 'text-accent bg-accent/10',
    badge: 'text-accent bg-accent/10',
    value: 'text-foreground',
  },
  success: {
    icon: 'text-emerald-600 bg-emerald-500/10',
    badge: 'text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20',
    value: 'text-emerald-700 dark:text-emerald-400',
  },
  danger: {
    icon: 'text-red-600 bg-red-500/10',
    badge: 'text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-900/20',
    value: 'text-red-600 dark:text-red-400',
  },
  warning: {
    icon: 'text-amber-600 bg-amber-500/10',
    badge: 'text-amber-700 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/20',
    value: 'text-amber-700 dark:text-amber-400',
  },
  info: {
    icon: 'text-blue-600 bg-blue-500/10',
    badge: 'text-blue-700 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20',
    value: 'text-blue-700 dark:text-blue-400',
  },
}

export const ValueCard: React.FC<ValueCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = 'primary',
  badge,
  metadata,
  className = '',
}) => {
  const cfg = colorConfig[color]
  const TrendIcon = color === 'success' ? TrendingUp : color === 'danger' ? TrendingDown : null
  const ResolvedIcon = Icon ?? TrendIcon

  return (
    <div className={cn('bg-card border border-border rounded-lg p-4 shadow-precision', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
            {title}
          </p>
          <p
            className={cn('text-xl font-semibold tabular-nums', cfg.value)}
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
          {metadata && metadata.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
              {metadata.map((item, i) => (
                <span key={i} className="text-xs text-muted-foreground">
                  {item.label ? `${item.label} ` : ''}{item.value}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {ResolvedIcon && (
            <div className={cn('p-2 rounded-md', cfg.icon)}>
              <ResolvedIcon className="h-4 w-4" />
            </div>
          )}
          {badge && (
            <span className={cn('text-xs font-semibold px-2 py-0.5 rounded-full', cfg.badge)}>
              {badge}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
